import { FC, useContext, useEffect } from 'react';
import { NotConcludedRoomsResponse } from '../types';
import { useHttpMutation, useHttpQuery } from '../../../api';
import { API_URLS } from '../consts';
import { Link, useNavigate } from 'react-router-dom';
import {
  BhabiLink,
  ErrorLayout,
  LoadingLayout,
  UserAvatar,
  useAlert,
} from '../../../shared/components';
import { BhabiStorage } from '@/shared/lib';
import { getUser } from '../../../redux/reducers';
import { IN_PLAY_ROUTE } from '../in-play';
import { LoggerService } from '../../../shared/logger';
import { Box, Paper, Typography, styled } from '@mui/material';
import {
  ActionButtonStyled,
  NotificationStyled,
} from './styled';
import { useRandomColor } from '../in-play/components/playground';
import { PUBLIC_PROFILE_ROUTE } from '../profile';
import { CREATE_ROOM_ROUTE } from '../create-room/route.definitions';
import {
  BhabiPagination,
  InvitePlayerPageLayout,
  PaginationSlotContext,
  usePaginationHelper,
} from '../invite-players/components';
import { dispatch } from '@/redux';

// ~100px per room card (Paper + inner grid + user avatars row + mt)
// ~170px total chrome (98px topbar/tabs + ~72px pagination footer)
const ROOMS_ROW_HEIGHT = 100;
const ROOMS_CHROME_PX = 186;

export const UserRooms: FC = () => {
  const { data, isLoading, isError } = useHttpQuery<
    NotConcludedRoomsResponse[]
  >('notConcludedRooms', API_URLS.NOT_CONCLUDED_ROOMS, 'GET');

  const { mutate: reJoinRoom } = useHttpMutation<
    { token: string },
    { roomID: string }
  >(API_URLS.RE_JOIN_ROOM, 'POST');

  const nav = useNavigate();
  const { addAlert } = useAlert();

  const onHandleReJoinRoom = (roomID: string) => () => {
    reJoinRoom(
      { roomID },
      {
        onSuccess: res => {
          BhabiStorage.setToken(res.token);
          dispatch(getUser(res.token));
          nav(IN_PLAY_ROUTE.ABSOLUTE_PATH);
        },
        onError: () => {
          LoggerService.error('Re-join room failed');
          addAlert({
            type: 'error',
            message: 'Re-join room failed',
          });
        },
      }
    );
  };

  const { page, setPage, paginatedData, setPageSize, pageSize } =
    usePaginationHelper(ROOMS_CHROME_PX, ROOMS_ROW_HEIGHT, data);

  const totalItems = data?.length ?? 0;
  const showPagination = totalItems > pageSize;

  const setPaginationContent = useContext(PaginationSlotContext);

  useEffect(() => {
    if (!setPaginationContent) return;
    setPaginationContent(
      showPagination ? (
        <BhabiPagination
          page={page}
          setPage={setPage}
          totalItems={totalItems}
          pageSize={pageSize}
          setPageSize={setPageSize}
          pageSizeOptions={[]}
        />
      ) : null
    );
    return () => setPaginationContent(null);
  }, [setPaginationContent, showPagination, page, setPage, totalItems, pageSize, setPageSize]);

  if (isLoading && !isError) {
    return <LoadingLayout inline text="Looking for your tables…" />;
  }

  if (isError) {
    return <ErrorLayout />;
  }

  if (!paginatedData?.length) {
    return (
      <div className="bh-state" style={{ minHeight: 240 }}>
        <div className="bh-state__inner">
          <p className="bh-h3">No tables waiting for you</p>
          <p className="bh-muted bh-small" style={{ margin: 0 }}>
            Tables you leave mid-game show up here so you can jump back in.
          </p>
          <Link
            to={CREATE_ROOM_ROUTE.ABSOLUTE_PATH}
            className="bh-btn bh-btn--primary bh-btn--small"
          >
            Create a table
          </Link>
        </div>
      </div>
    );
  }

  return (
    <InvitePlayerPageLayout>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-1">
        {paginatedData?.map((n, i) => (
          <NotificationStyled
            key={[n.roomID, i].join('-')}
            component={Paper}
          >
            <Box
              display="grid"
              gridTemplateColumns="1fr auto"
              alignItems="center"
              gap={1}
              p={1}
            >
              <Typography variant="body1" fontWeight={700}>
                Table waiting for you
              </Typography>
              <ActionButtonStyled
                variant="contained"
                color="primary"
                size="small"
                disabled={false}
                onClick={onHandleReJoinRoom(n.roomID)}
                aria-label="Rejoin this table"
              >
                Rejoin
              </ActionButtonStyled>
            </Box>
            <UsersInRoomList list={n.users} />
          </NotificationStyled>
        ))}
      </div>
    </InvitePlayerPageLayout>
  );
};

export interface UsersInRoomListProps {
  list: {
    email: string;
    userID: string;
    name: string;
    photoURL: string;
    state: 'online' | 'offline' | 'joined' | 'left' | 'rejoined';
  }[];
}

export const UsersInRoomList: FC<UsersInRoomListProps> = ({ list }) => {
  const avatarColors = useRandomColor(list.length);

  return (
    <PlayersInfoWrapper noOfPlayers={list.length}>
      {list.map((user, i) => (
        <Box key={i} display="flex" flexDirection="column" alignItems="center">
          <BhabiLink
            to={[
              PUBLIC_PROFILE_ROUTE.EXACT_PATH,
              '?user_id=',
              user.userID,
            ].join('')}
            style={{ textDecoration: 'none' }}
          >
            <UserAvatar
              {...{
                photoURL: user.photoURL,
                name: user.name,
                userState: user.state,
                avatarProps: {
                  sx: {
                    bgcolor: avatarColors[i],
                    width: 25,
                    height: 25,
                  },
                },
              }}
            />
          </BhabiLink>
        </Box>
      ))}
    </PlayersInfoWrapper>
  );
};
export interface PlayersWrapperProps {
  noOfPlayers: number;
}

export const PlayersInfoWrapper = styled(Box, {
  shouldForwardProp: props => props !== 'noOfPlayers',
})<PlayersWrapperProps>(({ theme, noOfPlayers }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${Math.min(noOfPlayers, 9)}, auto)`,
  gridTemplateRows: 'auto',
  width: 'fit-content',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
}));
